import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { exerciseMatchesDraft } from "../../utils/exerciseNameMatch";

function resolveCircuitRounds(exercise) {
  if (Array.isArray(exercise.circuit) && exercise.circuit.length > 0) {
    return exercise.circuit.length;
  }
  const r = exercise.rounds;
  if (typeof r === "number" && r > 0) return r;
  if (r !== undefined && r !== null && r !== "") {
    const n = parseInt(String(r), 10);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return 1;
}

function circuitTemplateNames(exercise) {
  if (Array.isArray(exercise.circuit) && exercise.circuit.length > 0) {
    const round0 = exercise.circuit[0];
    if (Array.isArray(round0)) {
      const names = round0
        .map((ex) => (ex?.name ? String(ex.name).trim() : ""))
        .filter(Boolean);
      if (names.length > 0) return names;
    }
  }
  if (Array.isArray(exercise.subExercises) && exercise.subExercises.length > 0) {
    return exercise.subExercises
      .map((sub) => (sub?.name ? String(sub.name).trim() : ""))
      .filter(Boolean);
  }
  return [];
}

function isCircuitExercise(exercise) {
  if (!exercise) return false;
  const t = String(exercise.exerciseType || exercise.type || "").toLowerCase();
  if (t === "single") return false;
  if (t === "circuit") return true;
  if (Array.isArray(exercise.circuit) && exercise.circuit.length > 0) {
    const r0 = exercise.circuit[0];
    return Array.isArray(r0) && r0.length > 0;
  }
  if (Array.isArray(exercise.subExercises) && exercise.subExercises.length > 0) {
    return true;
  }
  return false;
}

export function formatExerciseSummaryLines(exercise) {
  if (!exercise) return [];

  if (isCircuitExercise(exercise)) {
    const rounds = resolveCircuitRounds(exercise);
    const roundLabel = rounds === 1 ? "1 round" : `${rounds} rounds`;
    const names = circuitTemplateNames(exercise);
    if (names.length > 0) {
      return [`Circuit (${roundLabel}): ${names.join(", ")}`];
    }
    const parent = exercise.name && String(exercise.name).trim();
    if (parent) {
      return [`Circuit (${roundLabel}): ${parent}`];
    }
    return [`Circuit (${roundLabel})`];
  }

  const name = exercise.name && String(exercise.name).trim();
  if (name) return [name];
  return ["(Untitled exercise)"];
}

export default function WorkoutSummaryCard({
  workout,
  draftExerciseNames,
  selected,
  onClick,
  subtitle,
  theme,
  compact,
}) {
  const exercises = workout?.exercises || [];
  const name = workout?.name?.trim() || "Untitled workout";

  return (
    <Paper
      onClick={onClick}
      elevation={compact ? 0 : 1}
      sx={{
        p: compact ? 1.5 : 2,
        mb: compact ? 1 : 1.5,
        cursor: onClick ? "pointer" : "default",
        backgroundColor: selected ? theme.palette.primary.main : "white",
        color: selected ? "white" : "inherit",
        border: selected ? "none" : 1,
        borderColor: "divider",
      }}
    >
      {subtitle && (
        <Typography
          variant="caption"
          color={selected ? "rgba(255,255,255,0.85)" : "text.secondary"}
          display="block"
          sx={{ mb: 0.5 }}
        >
          {subtitle}
        </Typography>
      )}
      <Typography variant="body2" fontWeight={600}>
        {name}
      </Typography>
      {workout?.description?.trim() && (
        <Typography
          variant="body2"
          color={selected ? "rgba(255,255,255,0.75)" : "text.secondary"}
          sx={{ fontSize: "0.75rem", mt: 0.25, lineHeight: 1.4 }}
        >
          {workout.description.trim()}
        </Typography>
      )}
      {exercises.length === 0 ? (
        <Typography
          variant="body2"
          color={selected ? "rgba(255,255,255,0.75)" : "text.secondary"}
          sx={{ fontSize: "0.8rem", mt: 0.5 }}
        >
          No exercises
        </Typography>
      ) : (
        <Box
          component="ul"
          sx={{
            m: 0,
            mt: 0.5,
            pl: 2,
            "& li": { py: 0.1 },
          }}
        >
          {exercises.map((ex, exIdx) => {
            const lines = formatExerciseSummaryLines(ex);
            const lineHighlight = exerciseMatchesDraft(ex, draftExerciseNames);
            return lines.map((line, lineIdx) => (
              <li key={`ex-${exIdx}-${lineIdx}`}>
                <Typography
                  variant="body2"
                  color={selected ? "rgba(255,255,255,0.9)" : "text.secondary"}
                  component="span"
                  sx={{
                    fontSize: "0.8rem",
                    lineHeight: 1.45,
                    ...(lineHighlight && {
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      display: "inline-block",
                      bgcolor: selected
                        ? "rgba(255, 255, 255, 0.2)"
                        : theme.palette.mode === "dark"
                          ? "rgba(255, 167, 38, 0.22)"
                          : "rgba(237, 108, 2, 0.18)",
                    }),
                  }}
                >
                  {line}
                </Typography>
              </li>
            ));
          })}
        </Box>
      )}
    </Paper>
  );
}
